import React, { useState } from 'react';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { Card } from 'primereact/card';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Slider } from 'primereact/slider';
import { Divider } from 'primereact/divider';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';

const EmiCalculator = () => {
    const [formData, setFormData] = useState({
        loanAmount: 100000,
        interestRate: 12,
        loanTerm: 12,
        amortType: 'SIMPLE',
        paymentFrequency: 'Monthly',
        disbursementDate: new Date(),
        firstEmiDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        firstEmiDelayed: true
    });

    const [emiResult, setEmiResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = React.useRef(null);
    const API_BASE_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000';

    const amortTypeOptions = [
        { label: 'Simple Interest', value: 'SIMPLE' },
        { label: 'Reducing Balance', value: 'REDUCING' }
    ];

    const frequencyOptions = [
        { label: 'Monthly', value: 'Monthly' },
        { label: 'Weekly', value: 'Weekly' },
        { label: 'Bi-Weekly', value: 'BiWeekly' },
        { label: 'Daily', value: 'Daily' }
    ];

    const handleInputChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatDateForApi = (date) => {
        return date.toISOString().split('T')[0];
    };

    const calculateEMI = async () => {
        setLoading(true);
        setEmiResult(null);
        
        try {
            const requestData = {
                loanAmount: formData.loanAmount,
                interestRate: formData.interestRate,
                loanTerm: formData.loanTerm,
                amortType: formData.amortType,
                paymentFrequency: formData.paymentFrequency,
                disbursementDate: formatDateForApi(formData.disbursementDate),
                firstEmiDate: formData.firstEmiDelayed ? formatDateForApi(formData.firstEmiDate) : null,
                firstEmiDelayed: formData.firstEmiDelayed
            };

           const response = await fetch(`${API_BASE_URL}emi/calculate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Calculation failed');
            }

            const result = await response.json();
            
            // Convert string dates to Date objects for display
            if (result.schedule) {
                result.schedule = result.schedule.map(item => ({
                    ...item,
                    paymentDate: new Date(item.paymentDate)
                }));
            }
            
            setEmiResult(result);
            
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'EMI calculation completed',
                life: 3000
            });
            
        } catch (error) {
            console.error('Error calculating EMI:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to calculate EMI',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleQuickCalculate = async () => {
        setLoading(true);
        setEmiResult(null);
        
        try {
            const requestData = {
                loanAmount: formData.loanAmount,
                interestRate: formData.interestRate,
                loanTerm: formData.loanTerm,
                amortType: formData.amortType,
                paymentFrequency: formData.paymentFrequency
            };

            const API_BASE_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000';

            const response = await fetch(`${API_BASE_URL}emi/quick-calculate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Quick calculation failed');
            }

            const result = await response.json();
            setEmiResult(result);
            
            toast.current.show({
                severity: 'info',
                summary: 'Quick Result',
                detail: 'Quick EMI calculation completed',
                life: 3000
            });
            
        } catch (error) {
            console.error('Error in quick calculation:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to calculate EMI',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const resetCalculator = () => {
        setFormData({
            loanAmount: 100000,
            interestRate: 12,
            loanTerm: 12,
            amortType: 'SIMPLE',
            paymentFrequency: 'Monthly',
            disbursementDate: new Date(),
            firstEmiDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            firstEmiDelayed: true
        });
        setEmiResult(null);
        
        toast.current.show({
            severity: 'info',
            summary: 'Reset',
            detail: 'Calculator has been reset',
            life: 2000
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const principalPercentage = emiResult 
        ? (formData.loanAmount / emiResult.totalPayment * 100) 
        : 0;
    const interestPercentage = emiResult 
        ? (emiResult.totalInterest / emiResult.totalPayment * 100) 
        : 0;

    return (
        <div className="p-4">
            <Toast ref={toast} />
            
            <Card title="Loan EMI Calculator" className="shadow-2">
                <div className="grid">
                    {/* Left Column - Input Form */}
                    <div className="col-12 md:col-6">
                        <div className="grid formgrid">
                            <div className="col-12">
                                <h3 className="mb-3">Loan Details</h3>
                            </div>

                            {/* Loan Amount */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="loanAmount" className="font-bold block mb-2">
                                        Loan Amount (₹)
                                    </label>
                                    <InputNumber
                                        id="loanAmount"
                                        value={formData.loanAmount}
                                        onValueChange={(e) => handleInputChange('loanAmount', e.value)}
                                        mode="currency"
                                        currency="INR"
                                        locale="en-IN"
                                        min={1000}
                                        max={10000000}
                                        className="w-full"
                                    />
                                </div>
                                <Slider
                                    value={formData.loanAmount}
                                    onChange={(e) => handleInputChange('loanAmount', e.value)}
                                    min={1000}
                                    max={10000000}
                                    step={1000}
                                    className="mt-2"
                                />
                            </div>

                            {/* Interest Rate */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="interestRate" className="font-bold block mb-2">
                                        Annual Interest Rate (%)
                                    </label>
                                    <InputNumber
                                        id="interestRate"
                                        value={formData.interestRate}
                                        onValueChange={(e) => handleInputChange('interestRate', e.value)}
                                        min={0.1}
                                        max={30}
                                        step={0.1}
                                        suffix="%"
                                        className="w-full"
                                    />
                                </div>
                                <Slider
                                    value={formData.interestRate}
                                    onChange={(e) => handleInputChange('interestRate', e.value)}
                                    min={0.1}
                                    max={30}
                                    step={0.1}
                                    className="mt-2"
                                />
                            </div>

                            {/* Loan Term */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="loanTerm" className="font-bold block mb-2">
                                        Loan Term
                                    </label>
                                    <div className="flex">
                                        <InputNumber
                                            id="loanTerm"
                                            value={formData.loanTerm}
                                            onValueChange={(e) => handleInputChange('loanTerm', e.value)}
                                            min={1}
                                            max={360}
                                            className="flex-grow-1"
                                        />
                                        <Dropdown
                                            value={formData.paymentFrequency}
                                            options={frequencyOptions}
                                            onChange={(e) => handleInputChange('paymentFrequency', e.value)}
                                            className="ml-2 w-6"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Interest Type */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="amortType" className="font-bold block mb-2">
                                        Interest Type
                                    </label>
                                    <Dropdown
                                        id="amortType"
                                        value={formData.amortType}
                                        options={amortTypeOptions}
                                        onChange={(e) => handleInputChange('amortType', e.value)}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            {/* Disbursement Date */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="disbursementDate" className="font-bold block mb-2">
                                        Disbursement Date
                                    </label>
                                    <Calendar
                                        id="disbursementDate"
                                        value={formData.disbursementDate}
                                        onChange={(e) => handleInputChange('disbursementDate', e.value)}
                                        dateFormat="dd/mm/yy"
                                        className="w-full"
                                        showIcon
                                    />
                                </div>
                            </div>

                            {/* First EMI Delayed Checkbox */}
                            <div className="col-12 md:col-6">
                                <div className="field-checkbox">
                                    <Checkbox
                                        inputId="firstEmiDelayed"
                                        checked={formData.firstEmiDelayed}
                                        onChange={(e) => handleInputChange('firstEmiDelayed', e.checked)}
                                    />
                                    <label htmlFor="firstEmiDelayed" className="ml-2 font-bold">
                                        First EMI Delayed
                                    </label>
                                </div>
                            </div>

                            {/* First EMI Date (conditional) */}
                            {formData.firstEmiDelayed && (
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="firstEmiDate" className="font-bold block mb-2">
                                            First EMI Date
                                        </label>
                                        <Calendar
                                            id="firstEmiDate"
                                            value={formData.firstEmiDate}
                                            onChange={(e) => handleInputChange('firstEmiDate', e.value)}
                                            dateFormat="dd/mm/yy"
                                            className="w-full"
                                            showIcon
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Validation Message */}
                            {formData.loanTerm > 300 && (
                                <div className="col-12">
                                    <Message 
                                        severity="warn" 
                                        text="Loan term above 300 periods may result in very small EMIs" 
                                    />
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="col-12">
                                <Divider />
                                <div className="flex gap-3 flex-wrap">
                                    <Button
                                        label="Calculate Full Schedule"
                                        icon="pi pi-calculator"
                                        onClick={calculateEMI}
                                        loading={loading}
                                        className="flex-grow-1"
                                        disabled={!formData.loanAmount || !formData.loanTerm}
                                    />
                                    <Button
                                        label="Quick Calculate"
                                        icon="pi pi-bolt"
                                        onClick={handleQuickCalculate}
                                        loading={loading}
                                        className="p-button-success flex-grow-1"
                                        disabled={!formData.loanAmount || !formData.loanTerm}
                                    />
                                    <Button
                                        label="Reset"
                                        icon="pi pi-refresh"
                                        onClick={resetCalculator}
                                        className="p-button-secondary flex-grow-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Results */}
                    <div className="col-12 md:col-6">
                        {emiResult ? (
                            <>
                                <h3 className="mb-3">EMI Calculation Result</h3>
                                
                                {/* Summary Cards */}
                                <div className="grid">
                                    <div className="col-12 md:col-4">
                                        <Card className="bg-blue-50 border-blue-200">
                                            <div className="text-center">
                                                <div className="text-sm font-semibold text-blue-600">Monthly EMI</div>
                                                <div className="text-2xl font-bold text-blue-700 mt-2">
                                                    {formatCurrency(emiResult.emiAmount)}
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                    
                                    <div className="col-12 md:col-4">
                                        <Card className="bg-green-50 border-green-200">
                                            <div className="text-center">
                                                <div className="text-sm font-semibold text-green-600">Total Interest</div>
                                                <div className="text-2xl font-bold text-green-700 mt-2">
                                                    {formatCurrency(emiResult.totalInterest)}
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                    
                                    <div className="col-12 md:col-4">
                                        <Card className="bg-purple-50 border-purple-200">
                                            <div className="text-center">
                                                <div className="text-sm font-semibold text-purple-600">Total Payment</div>
                                                <div className="text-2xl font-bold text-purple-700 mt-2">
                                                    {formatCurrency(emiResult.totalPayment)}
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                </div>

                                {/* Payment Breakdown */}
                                <Panel header="Payment Breakdown" className="mt-4">
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <div className="text-center mb-3">
                                                <div className="text-lg font-bold">Principal vs Interest</div>
                                            </div>
                                            <div className="relative">
                                                <ProgressBar
                                                    value={principalPercentage}
                                                    style={{ height: '30px' }}
                                                    className="mb-2"
                                                />
                                                <div className="flex justify-between text-sm">
                                                    <span className="font-bold text-blue-600">
                                                        Principal: {formatCurrency(formData.loanAmount)}
                                                    </span>
                                                    <span className="font-bold text-green-600">
                                                        Interest: {formatCurrency(emiResult.totalInterest)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="col-12 md:col-6">
                                            <div className="text-center mb-3">
                                                <div className="text-lg font-bold">Ratio</div>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded">
                                                <div className="flex align-items-center mb-2">
                                                    <div className="w-4 h-4 bg-blue-500 mr-2 rounded"></div>
                                                    <span>Principal: {principalPercentage.toFixed(1)}%</span>
                                                </div>
                                                <div className="flex align-items-center">
                                                    <div className="w-4 h-4 bg-green-500 mr-2 rounded"></div>
                                                    <span>Interest: {interestPercentage.toFixed(1)}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Panel>

                                {/* Payment Schedule (only for full calculation) */}
                                {emiResult.schedule && emiResult.schedule.length > 0 && (
                                    <Panel header="Payment Schedule" className="mt-4">
                                        <DataTable
                                            value={emiResult.schedule}
                                            paginator
                                            rows={5}
                                            rowsPerPageOptions={[5, 10, 25]}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                            className="p-datatable-sm"
                                        >
                                            <Column field="period" header="Period" sortable></Column>
                                            <Column 
                                                field="paymentDate" 
                                                header="Date" 
                                                sortable
                                                body={(rowData) => formatDate(rowData.paymentDate)}
                                            ></Column>
                                            <Column 
                                                field="payment" 
                                                header="EMI" 
                                                sortable
                                                body={(rowData) => formatCurrency(rowData.payment)}
                                            ></Column>
                                            <Column 
                                                field="principal" 
                                                header="Principal" 
                                                sortable
                                                body={(rowData) => formatCurrency(rowData.principal)}
                                            ></Column>
                                            <Column 
                                                field="interest" 
                                                header="Interest" 
                                                sortable
                                                body={(rowData) => formatCurrency(rowData.interest)}
                                            ></Column>
                                            <Column 
                                                field="balance" 
                                                header="Balance" 
                                                sortable
                                                body={(rowData) => formatCurrency(rowData.balance)}
                                            ></Column>
                                        </DataTable>
                                    </Panel>
                                )}
                            </>
                        ) : (
                            <Card className="text-center p-6">
                                <i className="pi pi-calculator text-6xl text-blue-500 mb-4"></i>
                                <div className="text-xl font-bold text-gray-600 mb-2">
                                    EMI Calculator
                                </div>
                                <div className="text-gray-500">
                                    Enter loan details and click "Calculate" to see your EMI breakdown
                                </div>
                                {loading && (
                                    <div className="mt-4">
                                        <i className="pi pi-spin pi-spinner text-2xl text-blue-500"></i>
                                        <div className="mt-2">Calculating...</div>
                                    </div>
                                )}
                            </Card>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default EmiCalculator;
